import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { RiskMeter, ScoreBadge } from "./StockBits";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Minus, Check, X, ShoppingCart } from "lucide-react";
import { cn } from "../lib/utils";

function Bar({ label, value, weight, tint }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium">{label} <span className="text-xs text-muted-foreground">· {Math.round(weight * 100)}% weight</span></span>
        <span className="font-mono text-sm font-semibold">{value}</span>
      </div>
      <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
        <div className={cn("h-full rounded-full transition-all duration-700", tint)} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

export default function WhyThisPickDialog({ ticker, onClose, onTraded }) {
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shares, setShares] = useState(10);
  const [trading, setTrading] = useState(false);

  useEffect(() => {
    if (!ticker) { setData(null); return; }
    setLoading(true);
    api.get(`/scores/ticker/${ticker}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load stock details"))
      .finally(() => setLoading(false));
    // reward reading a breakdown (once per open) for signed-in users
    if (user) {
      api.post("/gamify/action", { action: "read_breakdown", ticker })
        .then(({ data }) => { if (data.awarded) refreshUser(); })
        .catch(() => {});
    }
  }, [ticker]); // eslint-disable-line

  const buy = async () => {
    if (!user) { toast.error("Sign in to paper-trade"); return; }
    setTrading(true);
    try {
      await api.post("/portfolio/trade", { ticker, side: "buy", shares: Number(shares) });
      await api.post("/gamify/action", { action: "paper_trade", ticker }).catch(() => {});
      toast.success(`Bought ${shares} ${ticker} (virtual)`);
      refreshUser();
      onTraded && onTraded();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Trade failed");
    } finally {
      setTrading(false);
    }
  };

  return (
    <Dialog open={!!ticker} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="why-this-pick-dialog">
        {loading || !data ? (
          <>
            <DialogHeader>
              <DialogTitle className="sr-only">Loading stock details</DialogTitle>
              <DialogDescription className="sr-only">Fetching the score breakdown.</DialogDescription>
            </DialogHeader>
            <div className="py-16 text-center text-muted-foreground">Loading…</div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between pr-6">
                <div>
                  <span className="font-mono text-xl">{data.ticker}</span>
                  <p className="text-sm font-normal text-muted-foreground">{data.company}</p>
                </div>
                <div className="text-right">
                  <ScoreBadge score={data.score} size="lg" testId="detail-score" />
                  <p className="text-xs text-muted-foreground mt-1 font-mono">${data.price}</p>
                </div>
              </DialogTitle>
              <DialogDescription className="sr-only">
                Score breakdown, risk meter and plain-English explanation for {data.ticker}.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 mt-2">
              <div className="space-y-3">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Score Breakdown</p>
                <Bar label="Momentum" value={data.momentum_score} weight={0.4} tint="bg-primary" />
                <Bar label="Moving-Average Trend" value={data.ma_trend_score} weight={0.35} tint="bg-sky-500" />
                <Bar label="RSI Position (guardrail)" value={data.rsi_score} weight={0.25} tint="bg-indigo-500" />
              </div>

              <RiskMeter level={data.risk_level} atrPct={data.atr_pct} />

              <div className="rounded-xl border border-border bg-secondary/40 p-4">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Why this pick?</p>
                <ul className="space-y-2">
                  {data.explanation.map((b, i) => (
                    <li key={i} className="text-sm flex gap-2 leading-relaxed">
                      <span className="text-primary mt-0.5">•</span>{b}
                    </li>
                  ))}
                </ul>
              </div>

              {data.track_record?.length > 0 && (
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">
                    Recent appearances in Top Picks
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {data.track_record.slice(0, 12).map((t, i) => (
                      <span key={i} title={`${t.date}: ${t.return_pct}% vs S&P ${t.spy_return_pct}%`}
                        className={cn("text-xs font-mono px-2 py-1 rounded-md border flex items-center gap-1",
                          t.beat_sp ? "text-emerald-500 border-emerald-500/30 bg-emerald-500/10"
                                    : "text-red-500 border-red-500/30 bg-red-500/10")}>
                        {t.beat_sp ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        {t.return_pct > 0 ? <TrendingUp className="h-3 w-3" /> : t.return_pct < 0 ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                        {t.return_pct}%
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">Shares</label>
                  <Input type="number" min="1" value={shares} data-testid="trade-shares-input"
                    onChange={(e) => setShares(e.target.value)} className="mt-1" />
                </div>
                <Button data-testid="detail-buy-btn" onClick={buy} disabled={trading}
                  className="mt-5 gap-1.5 bg-sky-500 hover:bg-sky-600 text-white">
                  <ShoppingCart className="h-4 w-4" />
                  Buy ~${(data.price * (Number(shares) || 0)).toFixed(0)}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground text-center">Virtual paper trade — no real money.</p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
